import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  fetchAccountingConnectionStatus,
  fetchAccountingSummary,
  fetchSubscriptionMetrics,
  startQuickBooksAuthorization
} from '../services/paymentService';
import crmClient from './crm/crmClient';
import './AccountingDashboard.css';

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const currentDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const formatDate = value => new Date(`${value}T12:00:00`)
  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const formatMonthLabel = value => new Date(`${value}T12:00:00`)
  .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const getReportPeriod = ({ periodType, month, year, customStartDate, customEndDate }) => {
  if (periodType === 'month') {
    const [monthYear, monthNumber] = month.split('-').map(Number);
    const endDay = new Date(monthYear, monthNumber, 0).getDate();
    return {
      startDate: `${month}-01`,
      endDate: `${month}-${String(endDay).padStart(2, '0')}`
    };
  }
  if (periodType === 'year_to_date') {
    return { startDate: `${new Date().getFullYear()}-01-01`, endDate: currentDate() };
  }
  if (periodType === 'year') {
    return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
  }
  if (periodType === 'all_time') {
    return { startDate: '1900-01-01', endDate: currentDate() };
  }
  return { startDate: customStartDate, endDate: customEndDate };
};

const formatCurrency = value => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'Not configured';
  return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const Metric = ({ label, detail, tone, value }) => (
  <div className={`accounting-metric accounting-metric--${tone}`}>
    <span>{label}</span>
    <strong>{formatCurrency(value)}</strong>
    <small>{detail}</small>
  </div>
);

const StatementRow = ({ label, category, total, accent, value }) => (
  <div className={`accounting-statement-row${total ? ' accounting-statement-row--total' : ''}${accent ? ` accounting-statement-row--${accent}` : ''}`}>
    <div>
      <span>{label}</span>
      {category && <small>{category}</small>}
    </div>
    <strong>{formatCurrency(value)}</strong>
  </div>
);

const ReconciliationRow = ({ label, value, emphasis }) => (
  <div className={`accounting-reconciliation-row${emphasis ? ' accounting-reconciliation-row--emphasis' : ''}`}>
    <span>{label}</span>
    <strong>{formatCurrency(value)}</strong>
  </div>
);

const AccountingDashboard = () => {
  const { isDarkMode } = useTheme();
  const [basis, setBasis] = useState('accrual');
  const [periodType, setPeriodType] = useState('month');
  const [month, setMonth] = useState(currentMonth());
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [customStartDate, setCustomStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [customEndDate, setCustomEndDate] = useState(currentDate());
  const [connection, setConnection] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [saas, setSaas] = useState(null);
  const [saasStripe, setSaasStripe] = useState(null);
  const { startDate, endDate } = getReportPeriod({
    periodType, month, year, customStartDate, customEndDate
  });

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    fetchAccountingConnectionStatus()
      .then(statusResponse => {
        if (!active) return null;
        const nextConnection = statusResponse && statusResponse.payload ? statusResponse.payload : null;
        setConnection(nextConnection);
        if (!nextConnection || !nextConnection.connected) {
          setSummary(null);
          return null;
        }
        return fetchAccountingSummary({ startDate, endDate, basis });
      })
      .then(summaryResponse => {
        if (!active || !summaryResponse) return;
        setSummary(summaryResponse.payload || null);
      })
      .catch(requestError => {
        if (!active) return;
        const message = requestError && requestError.response && requestError.response.data
          ? requestError.response.data.message
          : null;
        setError(message || 'Financial data could not be loaded.');
        setSummary(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [startDate, endDate, basis]);

  // SaaS subscription metrics (api-shipments entitlements) — independent of the
  // QuickBooks/Stripe accounting summary, so it loads even before QB is connected.
  useEffect(() => {
    let active = true;
    crmClient.subscriptionMetrics().then(payload => { if (active) setSaas(payload); }).catch(() => {});
    fetchSubscriptionMetrics().then(payload => { if (active) setSaasStripe(payload); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const connectQuickBooks = () => {
    setConnecting(true);
    setError('');
    startQuickBooksAuthorization().catch(requestError => {
      const message = requestError && requestError.response && requestError.response.data
        ? requestError.response.data.message
        : null;
      setError(message || 'QuickBooks authorization could not be started.');
      setConnecting(false);
    });
  };

  const profitAndLoss = summary && summary.profit_and_loss ? summary.profit_and_loss : {};
  const quickBooksProfitAndLoss = summary && summary.quickbooks_profit_and_loss
    ? summary.quickbooks_profit_and_loss
    : {};
  const balanceSheet = summary && summary.balance_sheet ? summary.balance_sheet : {};
  const ownerDistribution = summary && summary.owner_distribution ? summary.owner_distribution : {};
  const reservePolicy = ownerDistribution.reserve_policy || {};
  const reconciliation = summary && summary.reconciliation ? summary.reconciliation : {};
  const stripeReconciliation = reconciliation.stripe || {};
  const operationsReconciliation = reconciliation.operations || {};
  const reconciliationVariances = reconciliation.variances || {};
  const isConnected = Boolean(connection && connection.connected);
  const isConfigured = Boolean(connection && connection.configured);
  const obligationsDue = summary
    ? Number(balanceSheet.accounts_payable || 0) + Number(balanceSheet.credit_card_liabilities || 0)
    : null;
  const reservePolicyDetail = reservePolicy.period_start
    ? `80% of ${formatMonthLabel(reservePolicy.period_start)} delivery and subscription revenue`
    : '80% of prior-month delivery and subscription revenue';
  const themeStyle = {
    '--accounting-surface': isDarkMode ? '#27293d' : '#ffffff',
    '--accounting-surface-muted': isDarkMode ? '#202235' : '#f4f6f8',
    '--accounting-page': isDarkMode ? '#1e1f2f' : '#eef1f4',
    '--accounting-text': isDarkMode ? '#f7f8fa' : '#20242a',
    '--accounting-muted': isDarkMode ? '#aeb3c2' : '#66707a',
    '--accounting-border': isDarkMode ? '#3a3d55' : '#d9dee4'
  };

  return (
    <main className="content accounting-page" style={themeStyle}>
      <header className="accounting-header">
        <div>
          <div className="accounting-eyebrow">Private financial workspace</div>
          <h1>Accounting</h1>
          <p>Profitability, cash position, and owner distribution readiness.</p>
        </div>
        <div className="accounting-controls" aria-label="Accounting report controls">
          <label>
            <span>Range</span>
            <select value={periodType} onChange={event => setPeriodType(event.target.value)}>
              <option value="month">Month</option>
              <option value="year_to_date">Year to date</option>
              <option value="year">Full year</option>
              <option value="all_time">All time</option>
              <option value="custom">Custom range</option>
            </select>
          </label>
          {periodType === 'month' && <label><span>Month</span><input type="month" value={month} onChange={event => setMonth(event.target.value)} /></label>}
          {periodType === 'year' && <label><span>Year</span><input type="number" min="1900" max="2100" value={year} onChange={event => setYear(event.target.value)} /></label>}
          {periodType === 'custom' && <>
            <label><span>From</span><input type="date" value={customStartDate} onChange={event => setCustomStartDate(event.target.value)} /></label>
            <label><span>Through</span><input type="date" value={customEndDate} min={customStartDate} onChange={event => setCustomEndDate(event.target.value)} /></label>
          </>}
          <div className="accounting-basis">
            <span>Basis</span>
            <div className="accounting-segmented" role="group" aria-label="Accounting basis">
              <button type="button" className={basis === 'cash' ? 'active' : ''} onClick={() => setBasis('cash')}>Cash</button>
              <button type="button" className={basis === 'accrual' ? 'active' : ''} onClick={() => setBasis('accrual')}>Accrual</button>
            </div>
          </div>
        </div>
      </header>

      <section className="accounting-connection-strip" aria-label="Data connection status">
        <div>
          <i className={`tim-icons ${isConnected ? 'icon-check-2' : 'icon-alert-circle-exc'}`} aria-hidden="true" />
          <div>
            <strong>{isConnected ? `Connected to ${connection.company_name || 'QuickBooks'}` : 'Financial data is not connected yet'}</strong>
            <span>{isConnected ? 'Values come directly from QuickBooks reports for the selected period.' : 'Connect QuickBooks to populate the books.'}</span>
          </div>
        </div>
        {isConnected
          ? <div className="accounting-connection-actions">
              <span className="accounting-status">Connected</span>
              <button type="button" className="accounting-connect accounting-connect--secondary" disabled={connecting} onClick={connectQuickBooks}>
                {connecting ? 'Opening QuickBooks...' : 'Change QuickBooks'}
              </button>
            </div>
          : <button type="button" className="accounting-connect" disabled={!isConfigured || connecting} onClick={connectQuickBooks}>
              {connecting ? 'Opening QuickBooks...' : isConfigured ? 'Connect QuickBooks' : 'Setup required'}
            </button>}
      </section>

      {loading && <div className="accounting-notice">Loading financial data...</div>}
      {error && <div className="accounting-notice accounting-notice--error">{error}</div>}

      <div className="accounting-period-label">{formatDate(startDate)} – {formatDate(endDate)} · {basis === 'cash' ? 'Cash basis' : 'Accrual basis'}</div>

      <section className="accounting-metrics" aria-label="Financial summary">
        <Metric label="Revenue" value={summary ? profitAndLoss.revenue : null} detail="Completed deliveries and subscriptions" tone="revenue" />
        <Metric label="Contribution margin" value={summary ? profitAndLoss.contribution_margin : null} detail="Revenue less direct costs" tone="contribution" />
        <Metric label="Net income" value={summary ? profitAndLoss.net_income : null} detail="After all income and expenses" tone="income" />
        <Metric label="QuickBooks cash" value={summary ? balanceSheet.unrestricted_cash : null} detail="Bank accounts excluding Stripe clearing" tone="cash" />
        <Metric label="Available to owners" value={ownerDistribution.available_to_owners} detail="Requires reconciled bank balance" tone="distribution" />
      </section>

      {saas && saas.totals && (
        <>
          <div className="accounting-period-label">SaaS subscriptions · recurring revenue</div>
          <section className="accounting-metrics" aria-label="SaaS subscriptions">
            <div className="accounting-metric accounting-metric--revenue">
              <span>SaaS MRR</span>
              <strong>{formatCurrency(saasStripe ? saasStripe.mrr : saas.totals.mrr)}</strong>
              <small>{saasStripe ? `${formatCurrency(saasStripe.arr)} ARR · ${saasStripe.active_subscriptions} active Stripe subs` : `${formatCurrency(saas.totals.arr)} ARR · recurring subscription revenue`}</small>
            </div>
            <div className="accounting-metric accounting-metric--contribution">
              <span>Paid accounts</span>
              <strong>{saasStripe ? saasStripe.active_subscriptions : saas.totals.paid_stores}</strong>
              <small>billed Stripe subscriptions</small>
            </div>
            <div className="accounting-metric accounting-metric--income">
              <span>Grandfathered</span>
              <strong>{saas.totals.grandfathered}</strong>
              <small>founding ($0) — upgrade targets</small>
            </div>
            <div className="accounting-metric accounting-metric--cash">
              <span>Trialing</span>
              <strong>{saas.totals.trialing}</strong>
              <small>free trials in flight</small>
            </div>
            <div className="accounting-metric accounting-metric--distribution">
              <span>Upgrades · 30d</span>
              <strong>{saas.totals.upgrades_30d}</strong>
              <small>{saas.totals.upgrades_90d} in 90d</small>
            </div>
          </section>
        </>
      )}

      <section className="accounting-panel accounting-reconciliation" aria-label="Revenue reconciliation">
        <div className="accounting-panel-heading">
          <div><span>Revenue reconciliation</span><h2>Books compared with actual activity</h2></div>
          <span className="accounting-source">Selected period</span>
        </div>
        <p className="accounting-panel-intro">Gophr uses completion dates; Stripe uses charge and refund balance-transaction dates. Variances identify posting and collection timing and do not change QuickBooks.</p>
        <div className="accounting-reconciliation-grid">
          <div className="accounting-reconciliation-source">
            <div className="accounting-reconciliation-title"><strong>QuickBooks</strong><span>Ledger</span></div>
            <ReconciliationRow label="Revenue" value={summary ? quickBooksProfitAndLoss.revenue : null} emphasis />
            <small>{basis === 'cash' ? 'Cash-basis report revenue' : 'Accrual-basis report revenue'}</small>
          </div>
          <div className="accounting-reconciliation-source">
            <div className="accounting-reconciliation-title"><strong>Gophr operations</strong><span>{operationsReconciliation.available ? 'Live' : 'Unavailable'}</span></div>
            <ReconciliationRow label="Completed revenue" value={operationsReconciliation.available ? operationsReconciliation.gross_revenue : null} emphasis />
            <ReconciliationRow label="Driver pay" value={operationsReconciliation.available ? operationsReconciliation.driver_pay : null} />
            <ReconciliationRow label="Before processing" value={operationsReconciliation.available ? operationsReconciliation.contribution_before_processing : null} />
            <ReconciliationRow label="Variance to QuickBooks" value={operationsReconciliation.available ? reconciliationVariances.operations_less_quickbooks_revenue : null} />
            <small>{operationsReconciliation.available ? `${operationsReconciliation.completed_deliveries} completed deliveries` : 'Source could not be loaded'}</small>
          </div>
          <div className="accounting-reconciliation-source">
            <div className="accounting-reconciliation-title"><strong>Stripe</strong><span>{stripeReconciliation.available ? 'Live' : 'Unavailable'}</span></div>
            <ReconciliationRow label="Gross collections" value={stripeReconciliation.available ? stripeReconciliation.gross_collections : null} emphasis />
            <ReconciliationRow label="Refunds" value={stripeReconciliation.available ? stripeReconciliation.refunds : null} />
            <ReconciliationRow label="Net collections" value={stripeReconciliation.available ? stripeReconciliation.net_collections : null} />
            <ReconciliationRow label="Processing fees" value={stripeReconciliation.available ? stripeReconciliation.processing_fees : null} />
            <ReconciliationRow label="Net settlement" value={stripeReconciliation.available ? stripeReconciliation.net_settlement : null} />
            <ReconciliationRow label="Variance to QuickBooks" value={stripeReconciliation.available ? reconciliationVariances.stripe_collections_less_quickbooks_revenue : null} />
            <small>{stripeReconciliation.available ? `${stripeReconciliation.charge_count} charges · ${stripeReconciliation.refund_count} refunds` : 'Source could not be loaded'}</small>
          </div>
        </div>
      </section>

      <section className="accounting-main-grid">
        <div className="accounting-panel accounting-statement">
          <div className="accounting-panel-heading">
            <div><span>Profit and loss</span><h2>Operating performance</h2></div>
            <span className="accounting-source">Managerial</span>
          </div>
          <StatementRow label="Revenue" value={summary ? profitAndLoss.revenue : null} category="Delivery and subscription revenue earned" />
          <StatementRow label="Delivery revenue" value={summary ? profitAndLoss.delivery_revenue : null} category="Completed non-test deliveries" />
          <StatementRow label="Subscription revenue" value={summary ? profitAndLoss.subscription_revenue : null} category="Paid subscription service periods" />
          <StatementRow label="Driver costs" value={summary ? profitAndLoss.driver_costs : null} category="Driver pay tied to completed deliveries" />
          <StatementRow label="Processing fees" value={summary ? profitAndLoss.processing_fees : null} category="Fees tied to recognized delivery and subscription revenue" />
          <StatementRow label="Sales commissions" value={summary ? profitAndLoss.sales_commissions : null} category="Commissions tied to recognized sales" />
          <StatementRow label="Usage costs" value={summary ? profitAndLoss.usage_costs : null} category="Hosting and service usage tied to operations" />
          <StatementRow label="Cost of goods sold" value={summary ? profitAndLoss.variable_costs : null} category="Direct costs tied to revenue" />
          <StatementRow label="Contribution margin" value={summary ? profitAndLoss.contribution_margin : null} category="Revenue less direct costs" total accent="green" />
          <StatementRow label="Operating expenses" value={summary ? profitAndLoss.fixed_operating_costs : null} category="QuickBooks expense accounts" />
          <StatementRow label="Operating income" value={summary ? profitAndLoss.operating_profit : null} total />
          <StatementRow label="Other income and expense" value={summary ? profitAndLoss.other_income_expense : null} category="Includes interest, depreciation, taxes, and other activity" />
          <StatementRow label="Net income" value={summary ? profitAndLoss.net_income : null} total accent="orange" />
        </div>

        <div className="accounting-panel accounting-distribution">
          <div className="accounting-panel-heading">
            <div><span>Owner distribution</span><h2>Cash readiness</h2></div>
            <i className="tim-icons icon-money-coins" aria-hidden="true" />
          </div>
          <p className="accounting-panel-intro">Available to owners remains unavailable until the Chase ledger balance is reconciled to the connected bank balance.</p>
          <StatementRow label="Bank-account ledger cash" value={summary ? balanceSheet.unrestricted_cash : null} category="Excludes Stripe clearing" />
          <StatementRow label="Bills and cards due" value={obligationsDue} />
          <StatementRow label="Payroll and tax reserve" />
          <StatementRow label="Near-term debt service" />
          <StatementRow label="Operating reserve" value={ownerDistribution.operating_reserve} category={reservePolicyDetail} />
          <StatementRow label="Committed capital spending" />
          <StatementRow label="Available to owners" value={ownerDistribution.available_to_owners} total accent="blue" />
          <div className="accounting-readiness"><span>Month close</span><strong>{summary ? 'Bank reconciliation required' : 'Waiting for QuickBooks data'}</strong></div>
        </div>
      </section>

      <section className="accounting-detail-grid">
        <div className="accounting-panel">
          <div className="accounting-panel-heading"><div><span>Working capital</span><h2>Cash timing</h2></div></div>
          <StatementRow label="Accounts receivable" value={summary ? balanceSheet.accounts_receivable : null} category="Includes Net 30 customers" />
          <StatementRow label="Accounts payable" value={summary ? balanceSheet.accounts_payable : null} category="Bills not yet paid" />
          <StatementRow label="Credit card liabilities" value={summary ? balanceSheet.credit_card_liabilities : null} category="Purchases categorized when incurred" />
          <StatementRow label="Stripe clearing" value={summary ? balanceSheet.stripe_clearing_balance : null} category="Excluded from spendable cash" />
        </div>
        <div className="accounting-panel">
          <div className="accounting-panel-heading"><div><span>Balance sheet</span><h2>Financial position</h2></div></div>
          <StatementRow label="Total assets" value={summary ? balanceSheet.total_assets : null} />
          <StatementRow label="Total liabilities" value={summary ? balanceSheet.total_liabilities : null} category="Includes cards and loan principal" />
          <StatementRow label="Owner equity" value={summary ? balanceSheet.owner_equity : null} category="After prior distributions" total />
        </div>
        <div className="accounting-panel accounting-sources">
          <div className="accounting-panel-heading"><div><span>Source health</span><h2>Connections</h2></div></div>
          <div className="accounting-source-row"><strong>QuickBooks</strong><span>{isConnected ? 'Connected' : 'Not connected'}</span></div>
          <div className="accounting-source-row"><strong>Stripe</strong><span>{stripeReconciliation.available ? 'Live' : 'Unavailable'}</span></div>
          <div className="accounting-source-row"><strong>Gophr operations</strong><span>{operationsReconciliation.available ? 'Live' : 'Unavailable'}</span></div>
        </div>
      </section>
    </main>
  );
};

export default AccountingDashboard;